import { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import mqttLogApiService, {
  MqttLogScanSummary,
  MqttLogPreviewResponse,
  RabbitMqSessionInfo,
} from "../../services/mqttlog-api.service";
import RabbitMqAuthModal from "./components/RabbitMqAuthModal";
import logo from "../../assets/imgs/sysb_logo.svg";
import "./MqttLog.scss";

type FilterState = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  event: EventFilterValue;
  previewLimit: number;
  mainTopic: string;
  topicSuffix: string;
  clientId: string;
  publisherUsername: string;
};

type EventFilterValue = "" | "publish" | "deliver";

type FilterValidationState = {
  start: string;
  end: string;
};

const DEFAULT_PREVIEW_LIMIT = 1000;
const PREVIEW_LIMIT_OPTIONS = [1, 10, 100, 500, 1000, 2000, 3000] as const;
const ALL_MAIN_TOPICS = "__all__";
const JST_TIMEZONE = "Asia/Tokyo";
const BACKEND_OPERATION_TIMEOUT_SECONDS = 3600;
const EVENT_FILTER_OPTIONS: Array<{
  value: EventFilterValue;
  label: string;
}> = [
  { value: "", label: "指定しない" },
  { value: "publish", label: "publish" },
  { value: "deliver", label: "deliver" },
];

const getDefaultTopicSuffix = (mainTopic: string): string => {
  return mainTopic === "message" ? "7500" : "";
};

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateInputValueInTimeZone = (date: Date, timeZone: string): string => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    return toDateInputValue(date);
  }

  return `${year}-${month}-${day}`;
};

const normalizeTopicSuffix = (value: string, mainTopic: string): string => {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }

  const normalized = trimmed.replace(/^\./, "");
  if (normalized === mainTopic) {
    return "";
  }

  if (normalized.startsWith(`${mainTopic}.`)) {
    return normalized.slice(`${mainTopic}.`.length);
  }

  return normalized;
};

const buildTopicLabel = (mainTopic: string, topicSuffix: string): string => {
  if (mainTopic === ALL_MAIN_TOPICS) {
    return "指定しない";
  }

  const normalizedMainTopic = mainTopic.trim() || "message";
  const normalizedSuffix = normalizeTopicSuffix(
    topicSuffix,
    normalizedMainTopic,
  );
  return normalizedSuffix === ""
    ? `.${normalizedMainTopic}`
    : `.${normalizedMainTopic}.${normalizedSuffix}`;
};

const formatDateAndTimeLabel = (
  date: string,
  time: string,
): string | undefined => {
  const resolvedDate = date.trim();
  if (resolvedDate === "") {
    return undefined;
  }

  const resolvedTime = time.trim() === "" ? "00:00" : time.trim();
  return `${resolvedDate} ${resolvedTime}`;
};

const buildQueryDateTime = (
  date: string,
  time: string,
  options?: {
    fallbackDate?: string;
    defaultTime?: string;
    providedTimeSeconds?: string;
    defaultTimeSeconds?: string;
  },
): string | undefined => {
  const resolvedDate = date.trim() || options?.fallbackDate?.trim() || "";
  if (resolvedDate === "") {
    return undefined;
  }

  const trimmedTime = time.trim();
  const usedDefaultTime = trimmedTime === "";
  const resolvedTime = usedDefaultTime
    ? options?.defaultTime || "00:00"
    : trimmedTime;

  if (/^\d{2}:\d{2}$/.test(resolvedTime)) {
    const seconds = usedDefaultTime
      ? options?.defaultTimeSeconds || "00"
      : options?.providedTimeSeconds || "00";
    return `${resolvedDate} ${resolvedTime}:${seconds}`;
  }

  return `${resolvedDate} ${resolvedTime}`;
};

const buildFilterValidation = (filters: FilterState): FilterValidationState => {
  const startDate = filters.startDate.trim();
  const endDate = filters.endDate.trim();
  const endTime = filters.endTime.trim();

  return {
    start: startDate === "" ? "開始日は必須です。" : "",
    end:
      endDate === "" && endTime !== ""
        ? "終了時刻を指定する場合は終了日も入力してください。"
        : "",
  };
};

const buildInitialFilters = (): FilterState => {
  const today = new Date();
  return {
    startDate: toDateInputValue(today),
    startTime: "00:00",
    endDate: "",
    endTime: "",
    event: "",
    previewLimit: DEFAULT_PREVIEW_LIMIT,
    mainTopic: "message",
    topicSuffix: getDefaultTopicSuffix("message"),
    clientId: "",
    publisherUsername: "",
  };
};

const resolveAvailableMainTopics = (
  session: RabbitMqSessionInfo | null,
): string[] => {
  const candidates = session?.available_main_topics?.filter(
    (topic): topic is string =>
      typeof topic === "string" && topic.trim() !== "",
  );

  if (candidates && candidates.length > 0) {
    return Array.from(new Set(candidates));
  }

  return session?.can_view_system ? ["message", "system"] : ["message"];
};

const buildSelectableMainTopics = (availableMainTopics: string[]): string[] => {
  if (
    availableMainTopics.includes("message") &&
    availableMainTopics.includes("system")
  ) {
    return [ALL_MAIN_TOPICS, ...availableMainTopics];
  }

  return availableMainTopics;
};

const buildMainTopicOptionLabel = (mainTopic: string): string => {
  if (mainTopic === ALL_MAIN_TOPICS) {
    return "指定しない";
  }

  return `.${mainTopic}`;
};

const formatDuration = (totalSeconds: number): string => {
  const resolvedSeconds = Math.max(0, Math.floor(totalSeconds));
  if (resolvedSeconds < 60) {
    return `${resolvedSeconds}秒`;
  }

  const minutes = Math.floor(resolvedSeconds / 60);
  const seconds = resolvedSeconds % 60;
  if (seconds === 0) {
    return `${minutes}分`;
  }

  return `${minutes}分${seconds}秒`;
};

const formatEstimatedRangeTime = (
  scanSummary: MqttLogScanSummary | null,
  label: string,
): string => {
  if (!scanSummary) {
    return "";
  }

  if (scanSummary.file_count === 0) {
    return `${label} --`;
  }

  return `${label} 0~${formatDuration(scanSummary.estimated_seconds)}`;
};

const formatElapsedTime = (elapsedSeconds: number | null): string => {
  if (elapsedSeconds === null) {
    return "";
  }

  return `経過時間 ${formatDuration(elapsedSeconds)}`;
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== "object" || error === null) {
    return fallback;
  }

  const candidate = error as {
    message?: string;
    response?: {
      data?: {
        message?: string;
      };
    };
  };

  return candidate.response?.data?.message || candidate.message || fallback;
};

const isAbortLikeError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }

  if (typeof error !== "object" || error === null) {
    return false;
  }

  const candidate = error as { name?: string; code?: string; message?: string };
  return (
    candidate.name === "AbortError" ||
    candidate.name === "CanceledError" ||
    candidate.code === "ERR_CANCELED" ||
    candidate.message === "canceled"
  );
};

const getDownloadFileName = (contentDisposition: string | null): string => {
  if (!contentDisposition) {
    return "mqttlogs.zip";
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1] || "mqttlogs.zip";
};

const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const id = window.setTimeout(() => {
      resolve();
    }, ms);

    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(id);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
};

const buildCollapsedFilterSummary = (
  filters: FilterState,
  canViewOtherUsers: boolean,
): Array<{ label: string; value: string }> => {
  const items = [
    {
      label: "開始",
      value:
        formatDateAndTimeLabel(filters.startDate, filters.startTime) ||
        "未指定",
    },
    {
      label: "終了",
      value:
        formatDateAndTimeLabel(filters.endDate, filters.endTime) || "未指定",
    },
    {
      label: "トピック",
      value: buildTopicLabel(filters.mainTopic, filters.topicSuffix),
    },
    {
      label: "publish/deliver",
      value: filters.event || "指定しない",
    },
    {
      label: "取得件数",
      value: `${filters.previewLimit} 件`,
    },
    {
      label: "クライアント ID",
      value: filters.clientId.trim() || "未指定",
    },
  ];

  if (canViewOtherUsers) {
    items.push({
      label: "RabbitMQ ユーザー",
      value: filters.publisherUsername.trim() || "未指定",
    });
  }

  return items;
};

const clearTimeValue = (
  key: "startTime" | "endTime",
  setFilters: Dispatch<SetStateAction<FilterState>>,
) => {
  setFilters((prev) => ({
    ...prev,
    [key]: "",
  }));
};

const buildEffectiveEndDateTime = (
  endDate: string,
  endTime: string,
  fallbackDate: string,
): string => {
  return (
    buildQueryDateTime(endDate, endTime, {
      fallbackDate,
      defaultTime: "23:59",
      providedTimeSeconds: "59",
      defaultTimeSeconds: "59",
    }) || `${fallbackDate} 23:59:59`
  );
};

const buildEffectiveStartDateTime = (
  startDate: string,
  startTime: string,
): string | undefined => {
  return buildQueryDateTime(startDate, startTime, {
    defaultTime: "00:00",
    providedTimeSeconds: "00",
    defaultTimeSeconds: "00",
  });
};

const MqttLog = () => {
  const [filters, setFilters] = useState<FilterState>(() =>
    buildInitialFilters(),
  );
  const [isExtraFiltersOpen, setIsExtraFiltersOpen] = useState(false);
  const [rabbitUsernameInput, setRabbitUsernameInput] = useState("");
  const [rabbitPasswordInput, setRabbitPasswordInput] = useState("");
  const [rabbitSession, setRabbitSession] =
    useState<RabbitMqSessionInfo | null>(null);
  const [preview, setPreview] = useState<MqttLogPreviewResponse | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isAuthenticatingRabbit, setIsAuthenticatingRabbit] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewScanSummary, setPreviewScanSummary] =
    useState<MqttLogScanSummary | null>(null);
  const [downloadScanSummary, setDownloadScanSummary] =
    useState<MqttLogScanSummary | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [pageError, setPageError] = useState("");
  const [rabbitError, setRabbitError] = useState("");
  const [isRabbitAuthModalOpen, setIsRabbitAuthModalOpen] = useState(false);
  const [operationStatusLabel, setOperationStatusLabel] = useState("");
  const [activeOperation, setActiveOperation] = useState<
    "preview" | "download" | null
  >(null);
  const previewAbortRef = useRef<AbortController | null>(null);
  const downloadAbortRef = useRef<AbortController | null>(null);
  const estimateAbortRef = useRef<AbortController | null>(null);
  const isClearingAuthRef = useRef(false);
  const activePreviewJobIdRef = useRef<string | null>(null);
  const activeDownloadJobIdRef = useRef<string | null>(null);

  const hasRabbitAuth = rabbitSession?.authenticated === true;
  const canViewOtherUsers = rabbitSession?.can_view_other_users === true;
  const availableMainTopics = useMemo(
    () => resolveAvailableMainTopics(rabbitSession),
    [rabbitSession],
  );
  const selectableMainTopics = useMemo(
    () => buildSelectableMainTopics(availableMainTopics),
    [availableMainTopics],
  );
  const effectiveTopic = buildTopicLabel(
    filters.mainTopic,
    filters.topicSuffix,
  );
  const latestEndDate = useMemo(
    () => toDateInputValueInTimeZone(new Date(), JST_TIMEZONE),
    [],
  );
  const effectiveStartDateTime = useMemo(
    () => buildEffectiveStartDateTime(filters.startDate, filters.startTime),
    [filters.startDate, filters.startTime],
  );
  const effectiveEndDateTime = useMemo(
    () =>
      buildEffectiveEndDateTime(
        filters.endDate,
        filters.endTime,
        latestEndDate,
      ),
    [filters.endDate, filters.endTime, latestEndDate],
  );
  const effectiveDateTimeRangeLabel = useMemo(() => {
    if (!effectiveStartDateTime) {
      return "";
    }

    return `${effectiveStartDateTime} ~ ${effectiveEndDateTime}`;
  }, [effectiveEndDateTime, effectiveStartDateTime]);
  const collapsedFilterSummary = useMemo(
    () =>
      buildCollapsedFilterSummary(
        {
          ...filters,
          endDate: effectiveEndDateTime.slice(0, 10),
          endTime: effectiveEndDateTime.slice(11),
        },
        canViewOtherUsers,
      ),
    [canViewOtherUsers, effectiveEndDateTime, filters],
  );
  const filterValidation = useMemo(
    () => buildFilterValidation(filters),
    [filters],
  );
  const hasFilterValidationError =
    filterValidation.start !== "" || filterValidation.end !== "";
  const elapsedTimeLabel = useMemo(
    () => formatElapsedTime(elapsedSeconds),
    [elapsedSeconds],
  );
  const previewEstimateLabel = useMemo(
    () => formatEstimatedRangeTime(previewScanSummary, "表示目安"),
    [previewScanSummary],
  );
  const downloadEstimateLabel = useMemo(
    () => formatEstimatedRangeTime(downloadScanSummary, "ダウンロード目安"),
    [downloadScanSummary],
  );

  const isDownloadEstimatedToTimeout = useMemo(() => {
    if (!downloadScanSummary || downloadScanSummary.file_count === 0) {
      return false;
    }

    return (
      downloadScanSummary.estimated_seconds >= BACKEND_OPERATION_TIMEOUT_SECONDS
    );
  }, [downloadScanSummary]);

  const downloadDisabledMessage = useMemo(() => {
    if (!isDownloadEstimatedToTimeout) {
      return "";
    }

    return `取得範囲が広すぎるためダウンロードできません。期間を短くしてください。`;
  }, [isDownloadEstimatedToTimeout]);

  const operationSummaryLabel = useMemo(() => {
    return [previewEstimateLabel, downloadEstimateLabel, elapsedTimeLabel]
      .filter((label) => label !== "")
      .join(" / ");
  }, [downloadEstimateLabel, elapsedTimeLabel, previewEstimateLabel]);

  const rawLogLines = useMemo(() => {
    if (!preview?.items.length) {
      return [] as string[];
    }

    return preview.items.map((item) => item.raw_line);
  }, [preview]);

  useEffect(() => {
    if (
      filters.event !== "" ||
      filters.clientId.trim() !== "" ||
      filters.publisherUsername.trim() !== ""
    ) {
      setIsExtraFiltersOpen(true);
    }
  }, [filters.clientId, filters.event, filters.publisherUsername]);

  useEffect(() => {
    if (!selectableMainTopics.includes(filters.mainTopic)) {
      setFilters((prev) => ({
        ...prev,
        mainTopic: selectableMainTopics[0] || "message",
        topicSuffix: getDefaultTopicSuffix(
          selectableMainTopics[0] || "message",
        ),
      }));
    }
  }, [filters.mainTopic, selectableMainTopics]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const session = await mqttLogApiService.getSession();
        setRabbitSession(session);
        setRabbitUsernameInput(session.rabbitmq_username || "");
        setIsRabbitAuthModalOpen(session.authenticated !== true);
      } catch (error: unknown) {
        setRabbitSession({
          authenticated: false,
        });
        setIsRabbitAuthModalOpen(true);
        setPageError(
          getErrorMessage(
            error,
            "RabbitMQ 認証状態の取得に失敗しました。認証を入力してください。",
          ),
        );
      } finally {
        setIsPageLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    return () => {
      previewAbortRef.current?.abort();
      downloadAbortRef.current?.abort();
      estimateAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const base = String(import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

    const sendCancelKeepAlive = (
      type: "preview" | "download",
      jobId: string | null,
    ) => {
      if (!jobId) {
        return;
      }

      const headers = mqttLogApiService.getAuthHeaders();
      if (!headers) {
        return;
      }

      const url = `${base}/mqttlog/${type}-job/${encodeURIComponent(jobId)}/cancel`;
      try {
        fetch(url, {
          method: "POST",
          headers,
          keepalive: true,
        });
      } catch {
        // best-effort
      }
    };

    const handlePageHide = () => {
      if (isPreviewLoading) {
        sendCancelKeepAlive("preview", activePreviewJobIdRef.current);
      }
      if (isDownloading) {
        sendCancelKeepAlive("download", activeDownloadJobIdRef.current);
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
  }, [isDownloading, isPreviewLoading]);

  useEffect(() => {
    if (activeOperation === null) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setElapsedSeconds((prev) => (prev === null ? 1 : prev + 1));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [activeOperation, elapsedSeconds]);

  useEffect(() => {
    if (!hasRabbitAuth) {
      estimateAbortRef.current?.abort();
      setPreviewScanSummary(null);
      setDownloadScanSummary(null);
      setOperationStatusLabel("");
      return;
    }

    if (hasFilterValidationError) {
      estimateAbortRef.current?.abort();
      setPreviewScanSummary(null);
      setDownloadScanSummary(null);
      return;
    }

    if (isPreviewLoading || isDownloading) {
      return;
    }

    const timerId = window.setTimeout(async () => {
      const controller = new AbortController();
      estimateAbortRef.current?.abort();
      estimateAbortRef.current = controller;

      try {
        const estimateResponse = await mqttLogApiService.estimate(
          buildQuery(),
          {
            signal: controller.signal,
          },
        );
        setPreviewScanSummary(estimateResponse.preview_scan_summary);
        setDownloadScanSummary(estimateResponse.download_scan_summary);
      } catch (error: unknown) {
        if (!isAbortLikeError(error)) {
          setPreviewScanSummary(null);
          setDownloadScanSummary(null);
        }
      } finally {
        if (estimateAbortRef.current === controller) {
          estimateAbortRef.current = null;
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    filters.mainTopic,
    filters.previewLimit,
    canViewOtherUsers,
    filters.clientId,
    filters.endDate,
    filters.endTime,
    filters.event,
    filters.publisherUsername,
    filters.startDate,
    filters.startTime,
    filters.topicSuffix,
    hasFilterValidationError,
    hasRabbitAuth,
    isDownloading,
    isPreviewLoading,
  ]);

  const buildQuery = () => ({
    start: effectiveStartDateTime,
    end: effectiveEndDateTime,
    event: filters.event || undefined,
    mainTopic:
      filters.mainTopic === ALL_MAIN_TOPICS ? undefined : filters.mainTopic,
    subTopic:
      filters.mainTopic === ALL_MAIN_TOPICS
        ? undefined
        : normalizeTopicSuffix(filters.topicSuffix, filters.mainTopic) ||
          undefined,
    clientId: filters.clientId,
    publisherUsername: canViewOtherUsers
      ? filters.publisherUsername.trim() || undefined
      : undefined,
    limit: filters.previewLimit,
  });

  const loadPreview = async () => {
    if (isPreviewLoading || isDownloading) {
      return;
    }

    if (hasFilterValidationError) {
      return;
    }

    const controller = new AbortController();
    previewAbortRef.current?.abort();
    previewAbortRef.current = controller;
    setIsPreviewLoading(true);
    setActiveOperation("preview");
    setOperationStatusLabel("取得中");
    setPageError("");
    setElapsedSeconds(0);

    try {
      const query = buildQuery();

      const startResponse = await mqttLogApiService.startPreviewJob(query, {
        signal: controller.signal,
      });

      activePreviewJobIdRef.current = startResponse.job_id;
      setOperationStatusLabel("取得中");

      const previewDeadlineMs =
        typeof startResponse.expires_at === "number"
          ? startResponse.expires_at * 1000
          : null;

      while (true) {
        if (previewDeadlineMs !== null && Date.now() > previewDeadlineMs) {
          throw new Error(
            "ジョブの有効期限が切れました。もう一度実行してください。",
          );
        }

        const statusResponse = await mqttLogApiService.getPreviewJob(
          startResponse.job_id,
          { signal: controller.signal },
        );

        if (statusResponse.status === "done") {
          break;
        }

        if (statusResponse.status === "failed") {
          throw new Error(
            statusResponse.message || "ログの取得に失敗しました。",
          );
        }

        if (statusResponse.status === "canceled") {
          throw new DOMException("Aborted", "AbortError");
        }

        await sleep(1000, controller.signal);
      }

      const response = await mqttLogApiService.getPreviewJobResult(
        startResponse.job_id,
        { signal: controller.signal },
      );

      setPreview(response);
      setPreviewScanSummary(response.scan_summary);
      setOperationStatusLabel("取得完了");
    } catch (error: unknown) {
      if (isAbortLikeError(error)) {
        if (!isClearingAuthRef.current) {
          setPageError("ログ表示をキャンセルしました。");
        }
      } else {
        setPreview(null);
        setPageError(getErrorMessage(error, "ログの取得に失敗しました。"));
      }
      setOperationStatusLabel("");
      setElapsedSeconds(null);
    } finally {
      if (previewAbortRef.current === controller) {
        previewAbortRef.current = null;
      }
      activePreviewJobIdRef.current = null;
      setActiveOperation(null);
      setIsPreviewLoading(false);
    }
  };

  const handleRabbitAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    setRabbitError("");
    setPageError("");
    setIsAuthenticatingRabbit(true);

    try {
      const session = await mqttLogApiService.authenticateRabbit({
        rabbitmqUsername: rabbitUsernameInput,
        rabbitmqPassword: rabbitPasswordInput,
      });
      setRabbitSession(session);
      setRabbitUsernameInput(session.rabbitmq_username || rabbitUsernameInput);
      setRabbitPasswordInput("");
      setIsRabbitAuthModalOpen(false);

      setFilters((prev) => ({
        ...prev,
        mainTopic:
          buildSelectableMainTopics(resolveAvailableMainTopics(session))[0] ||
          "message",
        topicSuffix: getDefaultTopicSuffix(
          buildSelectableMainTopics(resolveAvailableMainTopics(session))[0] ||
            "message",
        ),
        publisherUsername: session.can_view_other_users
          ? prev.publisherUsername
          : "",
      }));
    } catch (error: unknown) {
      setRabbitSession({
        authenticated: false,
      });
      setPreview(null);
      setPreviewScanSummary(null);
      setDownloadScanSummary(null);
      setElapsedSeconds(null);
      setRabbitError(getErrorMessage(error, "RabbitMQ の認証に失敗しました。"));
    } finally {
      setIsAuthenticatingRabbit(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading || isPreviewLoading) {
      return;
    }

    if (hasFilterValidationError) {
      return;
    }

    if (isDownloadEstimatedToTimeout) {
      return;
    }

    const controller = new AbortController();
    downloadAbortRef.current = controller;
    setIsDownloading(true);
    setActiveOperation("download");
    setOperationStatusLabel("取得中");
    setPageError("");
    setElapsedSeconds(0);

    try {
      const query = buildQuery();

      const startResponse = await mqttLogApiService.startDownloadJob(query, {
        signal: controller.signal,
      });

      activeDownloadJobIdRef.current = startResponse.job_id;
      setOperationStatusLabel("取得中");

      const downloadDeadlineMs =
        typeof startResponse.expires_at === "number"
          ? startResponse.expires_at * 1000
          : null;

      while (true) {
        if (downloadDeadlineMs !== null && Date.now() > downloadDeadlineMs) {
          throw new Error(
            "ジョブの有効期限が切れました。もう一度実行してください。",
          );
        }

        const statusResponse = await mqttLogApiService.getDownloadJob(
          startResponse.job_id,
          { signal: controller.signal },
        );

        if (statusResponse.status === "done") {
          break;
        }

        if (statusResponse.status === "failed") {
          throw new Error(
            statusResponse.message || "ダウンロードに失敗しました。",
          );
        }

        if (statusResponse.status === "canceled") {
          throw new DOMException("Aborted", "AbortError");
        }

        await sleep(1000, controller.signal);
      }

      const response = await fetch(
        mqttLogApiService.getDownloadJobFileUrl(startResponse.job_id),
        {
          ...mqttLogApiService.getDownloadJobFileRequestInit(),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        let message = "ダウンロードに失敗しました。";
        try {
          const body = await response.json();
          if (typeof body?.message === "string" && body.message !== "") {
            message = body.message;
          }
        } catch {
          // Fall back to the default message if the error body is not JSON.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const fileName = getDownloadFileName(
        response.headers.get("content-disposition"),
      );
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);
      setOperationStatusLabel("取得完了");
    } catch (error: unknown) {
      if (isAbortLikeError(error)) {
        if (!isClearingAuthRef.current) {
          setPageError("ダウンロードをキャンセルしました。");
        }
      } else {
        setPageError(getErrorMessage(error, "ダウンロードに失敗しました。"));
      }
      setOperationStatusLabel("");
      setElapsedSeconds(null);
    } finally {
      if (downloadAbortRef.current === controller) {
        downloadAbortRef.current = null;
      }
      activeDownloadJobIdRef.current = null;
      setActiveOperation(null);
      setIsDownloading(false);
    }
  };

  const handleCancelActiveTask = () => {
    if (isPreviewLoading) {
      const jobId = activePreviewJobIdRef.current;
      if (jobId) {
        mqttLogApiService.cancelPreviewJob(jobId).catch(() => {
          // best-effort
        });
      }
      previewAbortRef.current?.abort();
      return;
    }

    if (isDownloading) {
      const jobId = activeDownloadJobIdRef.current;
      if (jobId) {
        mqttLogApiService.cancelDownloadJob(jobId).catch(() => {
          // best-effort
        });
      }
      downloadAbortRef.current?.abort();
    }
  };

  const handleClearDisplayedLogs = () => {
    if (isPreviewLoading || isDownloading) {
      return;
    }

    setPreview(null);
    setElapsedSeconds(null);
    setOperationStatusLabel("");
  };

  const handleClearRabbitAuth = async () => {
    isClearingAuthRef.current = true;

    if (isPreviewLoading && activePreviewJobIdRef.current) {
      mqttLogApiService
        .cancelPreviewJob(activePreviewJobIdRef.current)
        .catch(() => {
          // best-effort
        });
    }
    if (isDownloading && activeDownloadJobIdRef.current) {
      mqttLogApiService
        .cancelDownloadJob(activeDownloadJobIdRef.current)
        .catch(() => {
          // best-effort
        });
    }

    previewAbortRef.current?.abort();
    downloadAbortRef.current?.abort();
    estimateAbortRef.current?.abort();

    try {
      await mqttLogApiService.clearRabbitSession();
    } catch {
      // Reset local auth state even if the backend cleanup fails.
    }

    setIsPreviewLoading(false);
    setIsDownloading(false);
    setPageError("");

    setRabbitSession({ authenticated: false });
    setRabbitUsernameInput("");
    setRabbitPasswordInput("");
    setPreview(null);
    setPreviewScanSummary(null);
    setDownloadScanSummary(null);
    setElapsedSeconds(null);
    setActiveOperation(null);
    setOperationStatusLabel("");
    setFilters((prev) => ({
      ...prev,
      mainTopic: "message",
      topicSuffix: getDefaultTopicSuffix("message"),
      publisherUsername: "",
    }));
    setIsRabbitAuthModalOpen(true);

    activePreviewJobIdRef.current = null;
    activeDownloadJobIdRef.current = null;

    isClearingAuthRef.current = false;
  };

  if (isPageLoading) {
    return <div className="mqttLogPage loading">読み込み中...</div>;
  }

  return (
    <div className="mqttLogPage">
      <RabbitMqAuthModal
        isOpen={isRabbitAuthModalOpen}
        canClose={hasRabbitAuth}
        username={rabbitUsernameInput}
        password={rabbitPasswordInput}
        isSubmitting={isAuthenticatingRabbit}
        errorMessage={rabbitError}
        onUsernameChange={setRabbitUsernameInput}
        onPasswordChange={setRabbitPasswordInput}
        onSubmit={handleRabbitAuth}
        onClose={() => setIsRabbitAuthModalOpen(false)}
      />
      {hasRabbitAuth ? (
        <main className="mqttLogMain">
          <header className="mqttLogHeader">
            <div className="mqttLogHeaderLogo">
              <img src={logo} alt="" />
            </div>
            <div className="mqttLogHeaderTitle">
              <h1>RabbitMQ Trace Log</h1>
            </div>
            <div className="toolbarActions">
              <div className="headerUserName">
                {rabbitSession?.rabbitmq_username || ""}
              </div>
              <button
                type="button"
                className="ghostButton"
                onClick={handleClearRabbitAuth}
                disabled={!hasRabbitAuth}
              >
                ログアウト
              </button>
            </div>
          </header>

          <div className="mqttLogContentArea">
            {pageError ? (
              <div
                className="message error toast"
                role="alert"
                aria-live="assertive"
              >
                <span>{pageError}</span>
                <button
                  type="button"
                  className="toastCloseButton"
                  onClick={() => setPageError("")}
                  aria-label="エラーを閉じる"
                >
                  ×
                </button>
              </div>
            ) : null}

            <div
              className={`mqttLogWorkspace${isFilterCollapsed ? " isFilterCollapsed" : ""}`}
            >
              {!isFilterCollapsed ? (
                <section className="panel filterPanel">
                  <div className="panelHeader sortPanelHeader">
                    <div className="sectionTitleRow">
                      <div>
                        <h2>絞り込み条件</h2>
                      </div>
                    </div>
                  </div>

                  <>
                    <div className="filterGrid">
                      <div
                        className={`dateTimeFieldGroup${filterValidation.start ? " isInvalid" : ""}`}
                      >
                        <div className="dateTimeFieldGroupInputs">
                          <label>
                            開始日
                            <input
                              type="date"
                              value={filters.startDate}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  startDate: e.target.value,
                                }))
                              }
                              aria-invalid={filterValidation.start !== ""}
                            />
                          </label>
                          <label>
                            開始時刻
                            <div className="timeInputGroup">
                              <input
                                type="time"
                                step="1"
                                value={filters.startTime}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    startTime: e.target.value,
                                  }))
                                }
                                aria-invalid={filterValidation.start !== ""}
                              />
                              <button
                                type="button"
                                className="fieldClearButton"
                                onClick={() =>
                                  clearTimeValue("startTime", setFilters)
                                }
                                disabled={filters.startTime.trim() === ""}
                              >
                                削除
                              </button>
                            </div>
                          </label>
                        </div>
                        {filterValidation.start ? (
                          <div className="fieldErrorMessage" role="alert">
                            {filterValidation.start}
                          </div>
                        ) : null}
                      </div>
                      <div
                        className={`dateTimeFieldGroup${filterValidation.end ? " isInvalid" : ""}`}
                      >
                        <div className="dateTimeFieldGroupInputs">
                          <label>
                            終了日
                            <input
                              type="date"
                              value={filters.endDate}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  endDate: e.target.value,
                                }))
                              }
                              aria-invalid={filterValidation.end !== ""}
                            />
                          </label>
                          <label>
                            終了時刻
                            <div className="timeInputGroup">
                              <input
                                type="time"
                                step="1"
                                value={filters.endTime}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    endTime: e.target.value,
                                  }))
                                }
                                aria-invalid={filterValidation.end !== ""}
                              />
                              <button
                                type="button"
                                className="fieldClearButton"
                                onClick={() =>
                                  clearTimeValue("endTime", setFilters)
                                }
                                disabled={filters.endTime.trim() === ""}
                              >
                                削除
                              </button>
                            </div>
                          </label>
                        </div>
                        {filterValidation.end ? (
                          <div className="fieldErrorMessage" role="alert">
                            {filterValidation.end}
                          </div>
                        ) : (
                          <div className="fieldResolvedValue">
                            絞り込み対象: {effectiveDateTimeRangeLabel}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="filterStack">
                      <label className="fullWidthField topicField">
                        トピック
                        <div className="topicInputRow">
                          <select
                            value={filters.mainTopic}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                mainTopic: e.target.value,
                                topicSuffix: getDefaultTopicSuffix(
                                  e.target.value,
                                ),
                              }))
                            }
                            disabled={selectableMainTopics.length <= 1}
                          >
                            {selectableMainTopics.map((topic) => (
                              <option key={topic} value={topic}>
                                {buildMainTopicOptionLabel(topic)}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={filters.topicSuffix}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                topicSuffix: e.target.value,
                              }))
                            }
                            disabled={filters.mainTopic === ALL_MAIN_TOPICS}
                            placeholder={
                              filters.mainTopic === ALL_MAIN_TOPICS
                                ? "指定しない"
                                : filters.mainTopic === "message"
                                  ? "7500"
                                  : ""
                            }
                          />
                        </div>
                        <small className="topicHint">
                          絞り込み対象: {effectiveTopic}
                        </small>
                      </label>
                      <details
                        className="extraFiltersDetails"
                        open={isExtraFiltersOpen}
                        onToggle={(e) =>
                          setIsExtraFiltersOpen(
                            (e.currentTarget as HTMLDetailsElement).open,
                          )
                        }
                      >
                        <summary className="extraFiltersSummary">
                          詳細条件
                        </summary>
                        <div className="extraFiltersBody">
                          <label>
                            publish/deliver
                            <select
                              value={filters.event}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  event: e.target.value as EventFilterValue,
                                }))
                              }
                            >
                              {EVENT_FILTER_OPTIONS.map((option) => (
                                <option key={option.label} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            ログ表示件数
                            <select
                              value={String(filters.previewLimit)}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  previewLimit: Number(e.target.value),
                                }))
                              }
                            >
                              {PREVIEW_LIMIT_OPTIONS.map((limit) => (
                                <option key={limit} value={limit}>
                                  {limit} 件
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            クライアント ID
                            <input
                              type="text"
                              value={filters.clientId}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  clientId: e.target.value,
                                }))
                              }
                              placeholder=""
                            />
                          </label>
                          {canViewOtherUsers ? (
                            <label>
                              RabbitMQ ユーザー
                              <input
                                type="text"
                                value={filters.publisherUsername}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    publisherUsername: e.target.value,
                                  }))
                                }
                                placeholder=""
                              />
                            </label>
                          ) : null}
                        </div>
                      </details>
                    </div>

                    <div className="filterNote">
                      <div className="filterNotePrimary">
                        {rabbitSession?.rabbitmq_username
                          ? `${rabbitSession.rabbitmq_username} が閲覧可能なトピック: ${availableMainTopics.map((topic) => `.${topic}`).join(" / ")}`
                          : "認証ユーザーが閲覧可能なトピックのログを取得します。"}
                      </div>
                      {operationSummaryLabel ? (
                        <div className="filterMetric filterMetricSecondary">
                          <span className="filterMetricText">
                            {operationSummaryLabel}
                          </span>
                          {operationStatusLabel ? (
                            <span className="operationStatusLabel">
                              {operationStatusLabel}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    {downloadDisabledMessage ? (
                      <div className="fieldErrorMessage" role="alert">
                        {downloadDisabledMessage}
                      </div>
                    ) : null}
                    <div className="buttonRow">
                      <button
                        type="button"
                        className="primaryButton"
                        onClick={loadPreview}
                        disabled={isPreviewLoading || isDownloading}
                      >
                        {isPreviewLoading ? "取得中..." : "ログ表示"}
                      </button>
                      <div className="downloadActionGroup">
                        <button
                          type="button"
                          className="ghostButton"
                          onClick={handleDownload}
                          disabled={
                            isDownloading ||
                            isPreviewLoading ||
                            isDownloadEstimatedToTimeout
                          }
                        >
                          {isDownloading ? "ダウンロード中..." : "ダウンロード"}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="ghostButton dangerButton"
                        onClick={handleCancelActiveTask}
                        disabled={!isDownloading && !isPreviewLoading}
                      >
                        キャンセル
                      </button>
                    </div>
                  </>
                </section>
              ) : null}

              <section className="panel logPanel">
                <div className="panelHeader logPanelHeader">
                  <div className="logPanelHeaderMain">
                    <div className="sectionTitleRow">
                      <h2>ログ表示</h2>
                    </div>
                  </div>
                  {isFilterCollapsed ? (
                    <div className="collapsedFilterSummary" role="note">
                      {collapsedFilterSummary.map((item) => (
                        <div key={item.label} className="summaryItem">
                          <span className="summaryItemLabel">{item.label}</span>
                          <span className="summaryItemValue">{item.value}</span>
                        </div>
                      ))}
                      {operationSummaryLabel ? (
                        <div className="summaryItem summaryItemProgress">
                          <span className="summaryItemLabel">進行</span>
                          <span className="summaryItemValue">
                            {operationSummaryLabel}
                            {operationStatusLabel
                              ? ` / ${operationStatusLabel}`
                              : ""}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="logPanelHeaderSpacer" />
                  )}
                  <div className="logPanelHeaderControls">
                    <div className="resultCount resultCountAligned">
                      {preview ? `${preview.count} 件表示` : "未表示"}
                      {preview?.has_more ? " / 上限到達" : ""}
                    </div>
                    <button
                      type="button"
                      className="ghostButton"
                      onClick={handleClearDisplayedLogs}
                      disabled={
                        isPreviewLoading ||
                        isDownloading ||
                        !preview ||
                        preview.items.length === 0
                      }
                    >
                      ログクリア
                    </button>
                    <button
                      type="button"
                      className="toggleActionButton"
                      onClick={() => setIsFilterCollapsed((prev) => !prev)}
                      aria-label={
                        isFilterCollapsed
                          ? "絞り込み条件を開く"
                          : "絞り込み条件を閉じる"
                      }
                    >
                      <span className="toggleActionButtonLabel">
                        <span>条件を</span>
                        <span>{isFilterCollapsed ? "開く" : "閉じる"}</span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className="logViewport">
                  {rawLogLines.length > 0 ? (
                    <div className="logLineList">
                      {rawLogLines.map((line, index) => (
                        <div
                          key={`${index}-${line.slice(0, 24)}`}
                          className="logLine"
                          data-line={index + 1}
                        >
                          <span className="logLineText">{line}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="emptyState">
                      {preview
                        ? "取得結果は0件でした。"
                        : "ログ表示ボタンを押すと、ログを表示します。"}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </main>
      ) : null}
    </div>
  );
};

export default MqttLog;
